/**
 * `Ghost` — the two-ink misregistration. `docs/design-direction.md` §6.3.
 *
 * A second impression of the same word, offset 2px right and 1.5px up, printed in
 * `--accent-quiet` and screened behind the live text: a press that ran slightly out
 * of register. It is the most recognisable mark in the system and also the easiest to
 * ruin, so the contract fences it in hard — and this component is that fence:
 *
 *  · **Only on text at or above `--fs-28`.** Below 28px the two impressions blur into
 *    each other and read as a rendering fault rather than as print (§6.3, §12.12).
 *  · **At most one per screen.** A second concurrent mount does not print: it logs a
 *    developer error and renders its child untouched. "Never twice on one screen" is
 *    a review rule in the contract; here it is also a runtime one.
 *  · **Off in low ink.** `html[data-grain="off"]` strips the atmosphere and the ghost
 *    goes with the grain and the halftone (§6.1). `texture.css` hides the
 *    pseudo-element too, but a hidden blended element still costs a compositing
 *    layer, and the ghost is one of the three blend-mode elements a screen may have
 *    (§6.13) — so in low ink it is not rendered at all.
 *  · **Never different copy.** The ghost is `content: attr(data-ghost)`, and nothing
 *    may go in it that is not identical to the visible text. Development checks that.
 *
 * **How the size gate works, and why it is static.** The child must positively
 * declare a licensed size — either one of the four display tiers of §4.3 (`t-28`,
 * `t-34`, `t-44`, `t-64`) or an explicit inline `fontSize` — and anything else is
 * refused. The type-tier classes are the system's font-size API: the scale is a
 * closed set of nine, `font-size` never takes a literal length anywhere in the
 * codebase, and `type.css` already requires text to carry its tier class so the
 * font-failure degradation of §4.4 can find it. Reading the declared tier is
 * therefore both the honest check and a total one — it needs no layout, so it gives
 * the same answer before first paint, in a test, and on a device.
 *
 * **Shape of the API.** `Ghost` takes the element that carries the type and clones it
 * with the attribute, rather than wrapping it: the pseudo-element needs the *type
 * element's own box* to sit behind (`inset: 0` on a wrapper would offset the ghost
 * from the glyphs), and refusing then means literally handing the child back
 * untouched, with its classes and layout intact.
 *
 * The claim is taken on mount and released on unmount, and a `Ghost` that lost the
 * race does not take it over later: which element on a screen carries the ghost is a
 * composition decision, and moving it after the fact would print the loud mark
 * somewhere the layout never budgeted for it.
 *
 * `[data-display="fallback"]` disables the ghost in CSS as well — the mark depends on
 * precise glyph overlap, which a metric-matched fallback face does not give (§4.4).
 */

import { cloneElement, useEffect, useId, useState, useSyncExternalStore } from 'react';
import type { CSSProperties, JSX, ReactElement } from 'react';

/** `--fs-28` at the documented 16px root: the floor for a misregistered ghost. */
export const GHOST_MIN_PX = 28;

/**
 * The §4.3 type tiers, in px at the documented 16px root. `font-size` never takes a
 * literal length in this codebase, so a tier class is how a size is declared.
 */
const TIER_PX: Readonly<Record<string, number>> = {
  't-11': 11,
  't-13': 13,
  't-15': 15,
  't-17': 17,
  't-20': 20,
  't-28': 28,
  't-34': 34,
  't-44': 44,
  't-64': 64,
};

/**
 * The single live ghost, as a module-level claim, exposed as an external store.
 *
 * Deliberately not React state or context: "one per screen" is a property of the
 * document, two independent subtrees can each mount a `Ghost`, and the check has to
 * work without a provider that anyone could forget to render. `useSyncExternalStore`
 * is then how a component learns that it has become the holder — an effect updates
 * the external claim and the store notifies, rather than an effect calling `setState`
 * and cascading a render.
 */
let holder: string | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

function subscribeToClaim(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function readClaim(): string | null {
  return holder;
}

/** Take the claim if it is free (or already ours). Returns whether we hold it. */
function claim(token: string): boolean {
  if (holder === token) return true;
  if (holder !== null) return false;
  holder = token;
  notify();
  return true;
}

function release(token: string): void {
  if (holder !== token) return;
  holder = null;
  notify();
}

/** For tests: forget the current claim. Never called by application code. */
export function resetGhostClaim(): void {
  holder = null;
  notify();
}

/** The props a `Ghost` reads from — and writes to — the element it clones. */
interface GhostChildProps {
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  readonly children?: unknown;
  readonly 'data-ghost'?: string | undefined;
}

export interface GhostProps {
  /**
   * The ghosted copy. Must be identical to the visible text of `children` — the ghost
   * is a second impression of the same word, not a caption (§6.3).
   */
  readonly text: string;
  /** Exactly one element: the heading that carries the type. */
  readonly children: ReactElement;
}

/** A declared size in px, or `null` when the child does not declare one. */
export function declaredFontSizePx(props: {
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): number | null {
  const inline = props.style?.fontSize;
  if (typeof inline === 'number') return inline;
  if (typeof inline === 'string') {
    const match = /^(\d*\.?\d+)(px|rem|em)$/.exec(inline.trim());
    const value = match === null ? Number.NaN : Number.parseFloat(match[1] ?? '');
    if (Number.isFinite(value)) return match?.[2] === 'px' ? value : value * 16;
  }

  const classes = (props.className ?? '').split(/\s+/);
  let largest: number | null = null;
  for (const name of classes) {
    const px = TIER_PX[name];
    if (px !== undefined && (largest === null || px > largest)) largest = px;
  }
  return largest;
}

export function Ghost({ text, children }: GhostProps): JSX.Element {
  const token = useId();
  const [lowInk] = useState(() => document.documentElement.dataset.grain === 'off');
  const current = useSyncExternalStore(subscribeToClaim, readClaim, readClaim);

  const child = children as ReactElement<GhostChildProps>;
  const visible = child.props.children;
  const size = declaredFontSizePx(child.props);
  const bigEnough = size !== null && size >= GHOST_MIN_PX;
  const wanted = !lowInk && bigEnough;
  const owns = current === token;

  useEffect(() => {
    if (lowInk) return;

    if (size === null) {
      /*
       * Silence here would be a design regression nobody sees — the ghost simply
       * never prints. A tier below the floor is a legitimate refusal and stays quiet;
       * an undeclared size is a missing type class, and that is worth saying.
       */
      if (import.meta.env.DEV) {
        console.error(
          `Ghost: ${JSON.stringify(text)} declares no size, so no ghost is printed. ` +
            'Give the child its §4.3 type tier class (t-28 / t-34 / t-44 / t-64) — the ' +
            'type tier is how a font size is stated in this system — or an explicit ' +
            'inline fontSize.',
        );
      }
      return;
    }

    if (!bigEnough) return;

    if (!claim(token)) {
      if (import.meta.env.DEV) {
        console.error(
          `Ghost: a misregistration ghost is already mounted (${String(readClaim())}); ` +
            `${JSON.stringify(text)} will print flat. Only one ghost may exist per ` +
            'screen (design-direction.md §6.3, §12.12).',
        );
      }
      return;
    }

    if (import.meta.env.DEV && typeof visible === 'string' && visible !== text) {
      console.error(
        `Ghost: ghost copy ${JSON.stringify(text)} differs from the visible text ` +
          `${JSON.stringify(visible)}. The ghost is a second impression of the same ` +
          'word (design-direction.md §6.3).',
      );
    }

    return () => {
      release(token);
    };
  }, [lowInk, size, bigEnough, token, text, visible]);

  /*
   * Refused — low ink, too small, or someone else holds the claim: hand the child
   * straight back. No clone, no attribute, and no compositing layer for a blended
   * pseudo-element that would never be seen (§6.13).
   */
  if (!wanted || !owns) return children;

  const injected: GhostChildProps = { 'data-ghost': text };
  return cloneElement(child, injected);
}
